import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Pin, PinOff } from 'lucide-react'

/**
 * Shared writing-mode controls body (desktop panel + mobile sheet).
 * Presentational: actions are passed in so shells can share one component.
 */
export default function WritingModeControlsPanel({
  comparison,
  listId,
  listName,
  winnerId,
  submitting = false,
  onSelectWinner,
  onDeselectWinner,
  promptMe,
  reasoning,
  editorLocked = false,
  prompting = false,
  onNextComparison,
  // Desktop chrome
  showPin = false,
  pinned = false,
  onTogglePin,
  onMouseEnter,
  onMouseLeave,
  // Optional slot (font picker lands in polish ticket)
  fontPicker = null,
  className = '',
  style = undefined,
  fadingOut = false,
}) {
  const listLabel = listName || comparison?.list?.name || 'List'
  const resolvedListId = listId || comparison?.list_id || comparison?.list?.id

  return (
    <div
      className={`writing-mode-controls-panel ${className}`.trim()}
      role="region"
      aria-label="Writing controls"
      data-pinned={pinned ? 'true' : 'false'}
      data-fading-out={fadingOut ? 'true' : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        background: '#1a1a1a',
        color: '#e8e8e8',
        border: '1px solid #333',
        borderRadius: '0.5rem',
        padding: '0.75rem 1rem',
        minWidth: '16rem',
        maxWidth: '22rem',
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
        transition: fadingOut ? 'opacity 600ms ease' : undefined,
        opacity: fadingOut ? 0 : 1,
        ...style,
      }}
    >
      <div
        className="writing-mode-controls-header d-flex justify-content-between align-items-center mb-2"
        style={{ borderBottom: '1px solid #333', paddingBottom: '0.4rem' }}
      >
        <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>Controls</span>
        {showPin && (
          <button
            type="button"
            className="btn btn-sm btn-outline-light"
            onClick={onTogglePin}
            aria-label={pinned ? 'Unpin controls' : 'Pin controls'}
            aria-pressed={pinned}
            title={pinned ? 'Unpin' : 'Pin open'}
          >
            {pinned ? <PinOff size={14} /> : <Pin size={14} />}
            <span className="ms-1">{pinned ? 'Pinned' : 'Pin'}</span>
          </button>
        )}
      </div>

      <div className="d-flex flex-column gap-2">
        {resolvedListId != null && (
          <Link
            to={`/lists/${resolvedListId}`}
            className="btn btn-sm btn-outline-light text-start"
            style={{ textDecoration: 'none' }}
          >
            <ArrowLeft size={14} className="me-1" style={{ verticalAlign: '-2px' }} />
            List: {listLabel}
          </Link>
        )}

        {typeof onNextComparison === 'function' && (
          <button
            type="button"
            className="btn btn-sm btn-outline-light text-start"
            onClick={onNextComparison}
          >
            Next random comparison
          </button>
        )}

        <div className="mt-1">
          <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '0.35rem' }}>Winner</div>
          <div className="d-flex flex-column gap-1">
            <button
              type="button"
              className={`btn btn-sm w-100 ${comparison?.a_id == winnerId ? 'btn-primary' : 'btn-outline-light'}`}
              disabled={submitting}
              onClick={() =>
                comparison?.a_id == winnerId
                  ? onDeselectWinner?.()
                  : onSelectWinner?.(comparison?.a?.id ?? comparison?.a_id)
              }
            >
              {comparison?.a?.name || 'Option A'}
            </button>
            <button
              type="button"
              className={`btn btn-sm w-100 ${comparison?.b_id == winnerId ? 'btn-primary' : 'btn-outline-light'}`}
              disabled={submitting}
              onClick={() =>
                comparison?.b_id == winnerId
                  ? onDeselectWinner?.()
                  : onSelectWinner?.(comparison?.b?.id ?? comparison?.b_id)
              }
            >
              {comparison?.b?.name || 'Option B'}
            </button>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-sm btn-outline-secondary border"
          onClick={() => promptMe?.(reasoning)}
          disabled={editorLocked || prompting}
        >
          {prompting ? 'Looking for the right question ...' : "I'm stuck — Ask me something"}
        </button>

        {fontPicker}
      </div>
    </div>
  )
}
